import { useCallback, useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import DialogoLista from "@/components/listini/DialogoLista";
import { eAnteprima, eScaduto, listaDaMessaggio, ricordaOrigineListini } from "@/lib/listini";
import { erroreDi } from "@/lib/crm";
import { useMedia } from "@/hooks/use-media";

/* «Listini»: l'app di Aleandro («Ricerca Listini Arredo») aperta dentro il gestionale. Il suo codice non cambia:
   la serve il backend (backend/listini.py) con un «ponte» che la lega al resto (utente, tema, «Salva la lista»).
   Qui: il biglietto (60 secondi, l'indirizzo dell'iframe), l'iframe, i messaggi che il ponte manda, il tema.
   Il perché di ogni scelta: clienti/ida/piattaforma/prompt-01-10/c35-listini-aleandro-PIANO.md. */

const SANDBOX = "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads allow-forms allow-top-navigation-by-user-activation";

/** Il tema del gestionale: la classe `.dark` sul suo <html>. L'iframe lo segue, non segue il sistema operativo. */
function useTemaGestionale() {
  const leggi = () => (document.documentElement.classList.contains("dark") ? "dark" : "light");
  const [tema, setTema] = useState(leggi);
  useEffect(() => {
    const o = new MutationObserver(() => setTema(leggi()));
    o.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => o.disconnect();
  }, []);
  return tema;
}

export default function ListiniPage() {
  const tema = useTemaGestionale();
  const telefono = !useMedia("(min-width: 768px)");
  const temaIniziale = useRef(tema);
  const frame = useRef(null);
  const ultimoBiglietto = useRef(0);
  const [stato, setStato] = useState({ fase: "carico" });
  const [pronto, setPronto] = useState(false);
  const [lista, setLista] = useState(null);
  const anteprima = eAnteprima(window.location.hostname);

  const apri = useCallback(async () => {
    setStato({ fase: "carico" });
    setPronto(false);
    ultimoBiglietto.current = Date.now();
    try {
      const { data } = await api.post("/listini/biglietto", { tema: temaIniziale.current });
      const origine = new URL(data.url).origin;
      ricordaOrigineListini(origine); // il logout del gestionale chiude anche i listini
      setStato({ fase: "ok", url: data.url, origine });
    } catch (e) {
      setStato(e?.response?.status === 403 ? { fase: "negato" } : { fase: "errore", testo: erroreDi(e) });
    }
  }, []);

  useEffect(() => {
    if (!anteprima) apri();
  }, [apri, anteprima]);

  // I messaggi del ponte. Si ascolta solo l'iframe nostro, dall'origine del biglietto.
  useEffect(() => {
    const ascolta = (ev) => {
      const finestra = frame.current?.contentWindow;
      const nuova = listaDaMessaggio(ev, finestra, stato.origine);
      if (nuova) return setLista(nuova);
      // Accesso scaduto: un biglietto nuovo, ma non più di uno ogni 20 secondi (se il cookie non passa, niente giri a vuoto).
      if (eScaduto(ev, finestra) && Date.now() - ultimoBiglietto.current > 20_000) apri();
    };
    window.addEventListener("message", ascolta);
    return () => window.removeEventListener("message", ascolta);
  }, [stato.origine, apri]);

  // Il tema cambia senza ricaricare l'app. Si manda a iframe caricato: prima la sua finestra è ancora about:blank
  // (della nostra origine) e il browser rifiuta il messaggio; il tema di partenza lo porta già il biglietto.
  useEffect(() => {
    if (pronto) frame.current?.contentWindow?.postMessage({ tipo: "ida-tema", tema }, stato.origine);
  }, [tema, pronto, stato.origine]);

  return (
    <div className="grid gap-card" data-testid="listini-pagina">
      <PageHeader
        title="Listini"
        description={telefono ? undefined : "I listini dei fornitori, i configuratori e la lista da portare al cliente. Quando serve, la lista si salva nella sua scheda."}
      />

      {anteprima ? (
        <div data-testid="listini-anteprima">
          <EmptyState icon={Lock} tone="neutral" title="Da questo indirizzo i listini non si aprono">
            Sono dietro l'accesso del gestionale e il browser non lo passa da un'anteprima. Apri il gestionale da gestionale.internidautore.com.
          </EmptyState>
        </div>
      ) : stato.fase === "negato" ? (
        <div data-testid="listini-negato">
          <EmptyState icon={Lock} tone="neutral" title="I listini non sono nel tuo profilo">
            Se ti servono, chiedilo al titolare: li accende da Impostazioni, in Collaboratori e permessi.
          </EmptyState>
        </div>
      ) : stato.fase === "errore" ? (
        <div data-testid="listini-errore">
          <ErrorState title="I listini non si aprono" actions={<Button onClick={apri}>Riprova</Button>}>
            {stato.testo}
          </ErrorState>
        </div>
      ) : (
        <div className="relative h-[calc(100dvh-15rem)] min-h-[520px] overflow-hidden rounded-lg border border-line bg-surface max-md:h-[calc(100dvh-12.5rem)]">
          {stato.fase === "ok" ? (
            <iframe
              ref={frame}
              src={stato.url}
              title="Listini"
              allow="clipboard-write"
              sandbox={SANDBOX}
              onLoad={() => setPronto(true)}
              className="size-full border-0"
              data-testid="listini-iframe"
            />
          ) : null}
          {!pronto ? (
            <div className="absolute inset-0 bg-surface" data-testid="listini-carico">
              <ListSkeleton rows={5} label="Apro i listini…" />
            </div>
          ) : null}
        </div>
      )}

      {lista ? <DialogoLista lista={lista} onChiudi={() => setLista(null)} /> : null}
    </div>
  );
}
